// src/helpers/contentfulHelper.js
import { createClient } from 'contentful';

const client = createClient({
  space: process.env.REACT_APP_CONTENTFUL_SPACE_ID,
  accessToken: process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN,
  // DEV/TEST read the `test` environment so new content never reaches PROD before release.
  environment: process.env.REACT_APP_CONTENTFUL_ENVIRONMENT || 'master',
});

export const fetchNewsItems = async () => {
  try {
    const entries = await client.getEntries({
      content_type: 'newsItem',
      order: '-fields.date',
    });

    return entries.items;
  } catch (error) {
    console.error('Error fetching news items:', error);
    return [];
  }
};

export const fetchSupportDocumentLinks = async () => {
    try {
        const entries = await client.getEntries({
        content_type: 'supportDocumentLink',
        order: 'fields.name', 
        });

        return entries.items;
    } catch (error) {
        console.error('Error fetching support document links:', error);
        return [];
    }
}

export const fetchSupportVideos = async () => {
  try {
    const entries = await client.getEntries({
      content_type: 'supportVideo', 
      order: 'fields.title',
    });

    return entries.items;
  } catch (error) {
    console.error('Error fetching support videos:', error);
    return [];
  }
};

// Tutorial videos are published to /videos/ on the portal host by the publish-videos workflow
// (media/videos/). Contentful only stores the name, e.g. "faq-contact-quality-v1".
const VIDEO_NAME = /^[a-z0-9][a-z0-9-]*$/i;

export const tutorialVideo = name => {
  const trimmed = typeof name === 'string' ? name.trim() : '';
  if (!VIDEO_NAME.test(trimmed)) return null;
  return { src: `/videos/${trimmed}.mp4`, poster: `/videos/${trimmed}.jpg` };
};

// Groups by `order`, then title; entries without an order (PROD before the restructure) go last.
const compareGroups = (a, b) => {
  const aOrder = Number.isFinite(a.order) ? a.order : Infinity;
  const bOrder = Number.isFinite(b.order) ? b.order : Infinity;
  if (aOrder !== bOrder) return aOrder < bOrder ? -1 : 1;
  return (a.title || '').localeCompare(b.title || '');
};

export const toFaqGroups = items =>
  (items || [])
    .filter(group => group?.fields)
    .map(group => ({
      id: group.sys.id,
      title: group.fields.title,
      titlePt: group.fields.titlePt,
      order: group.fields.order,
      featureFlag: typeof group.fields.featureFlag === 'string' ? group.fields.featureFlag.trim() : '',
      faqs: (group.fields.faq || [])
        // Unresolved links (unpublished questions) come back without fields.
        .filter(faq => faq?.fields)
        .map(faq => ({
          id: faq.sys.id,
          anchor: faq.fields.anchor || faq.sys.id,
          title: faq.fields.title,
          titlePt: faq.fields.titlePt,
          copy: faq.fields.copy,
          copyPt: faq.fields.copyPt,
          video: tutorialVideo(faq.fields.videoName),
          videoMobile: tutorialVideo(faq.fields.videoMobileName),
        })),
    }))
    .sort(compareGroups);

export const fetchFaqGroups = async () => {
  try {
    const { items } = await client.getEntries({
      content_type: 'faqs',
      include: 2,
    });

    return toFaqGroups(items);
  } catch (error) {
    console.error('Error fetching FAQ groups:', error);
    return [];
  }
};
